"use client";

import { Search } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Image } from "./image";
import { Input } from "./input";

export type GifItem = {
	id: string;
	url: string;
	title: string;
	tags: string[];
};

type GifPickerProps = {
	gifs: GifItem[];
	onGifSelect: (gif: GifItem) => void;
	className?: string;
};

export function GifPicker({ gifs, onGifSelect, className }: GifPickerProps) {
	const [searchQuery, setSearchQuery] = useState("");

	const filteredGifs = gifs.filter((gif) => {
		const query = searchQuery.toLowerCase();
		return (
			gif.title.toLowerCase().includes(query) ||
			gif.tags.some((tag) => tag.toLowerCase().includes(query))
		);
	});

	return (
		<div
			className={cn("flex h-[382px] w-[320px] flex-col bg-popover", className)}
		>
			{/* Search */}
			<div className="flex-none border-b p-2">
				<div className="relative">
					<Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2 size-4 text-muted-foreground" />
					<Input
						autoFocus
						className="h-8 pl-8 text-sm"
						onChange={(e) => setSearchQuery(e.target.value)}
						placeholder="Search GIFs..."
						value={searchQuery}
					/>
				</div>
			</div>

			{/* GIF Grid */}
			<div className="scrollbar-track-[transparent] scrollbar-thumb-secondary-foreground/30 flex-1 overflow-y-auto p-2">
				{filteredGifs.length === 0 ? (
					<div className="flex h-full items-center justify-center text-muted-foreground text-sm">
						No GIFs found
					</div>
				) : (
					<div className="grid grid-cols-2 gap-2">
						{filteredGifs.map((gif) => (
							<button
								className="group relative aspect-square overflow-hidden rounded-md bg-muted transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary"
								key={gif.id}
								onClick={() => onGifSelect(gif)}
								title={gif.title}
								type="button"
							>
								<Image
									alt={gif.title}
									className="size-full object-cover"
									height={150}
									loading="lazy"
									src={gif.url}
									width={150}
								/>
								<div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
									<p className="truncate text-white text-xs">{gif.title}</p>
								</div>
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
